import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../APIs";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Search, Settings } from "lucide-react";
import {
    FaGitlab,
    FaGithub,
    FaJira,
    FaGoogleDrive,
    FaSlack,
    FaMicrosoft,
    FaConfluence,
    FaGit,
    FaSalesforce,
    FaDropbox,
    FaBitbucket,
    FaTrello,
    FaAtlassian,
} from "react-icons/fa6";
import { SiOkta, SiGooglecloud, SiMiro, SiNotion, SiZendesk, SiZoom } from "react-icons/si";
import GitlabData from "./Gitlab/GitlabData";
import GithubData from "./Github/GithubData";

interface SaaSSettingCard {
    id: string;
    name: string;
    description: string;
    icon: any;
    category: string;
    saasEnum: string;
}

const SAAS_APPS: SaaSSettingCard[] = [
    {
        id: "gitlab",
        name: "GitLab",
        description: "View synced GitLab users, groups, projects, memberships, and event logs for configured tenant instances",
        icon: FaGitlab,
        category: "SaaS Dev Platform",
        saasEnum: "GITLAB",
    },
    {
        id: "github",
        name: "GitHub",
        description: "View synced GitHub org members, teams, repositories, permissions, and audit events for configured tenant instances",
        icon: FaGithub,
        category: "SaaS Dev Platform",
        saasEnum: "GITHUB",
    },
    {
        id: "bitbucket",
        name: "Bitbucket",
        description: "View synced Bitbucket workspaces, users, groups, repositories, permissions, and activity metadata",
        icon: FaBitbucket,
        category: "SaaS Dev Platform",
        saasEnum: "BITBUCKET",
    },
    {
        id: "jira",
        name: "Jira",
        description: "View synced Jira users, groups, project roles, admin changes, and issue activity metadata",
        icon: FaJira,
        category: "SaaS Collaboration",
        saasEnum: "JIRA",
    },
    {
        id: "confluence",
        name: "Confluence",
        description: "View synced Confluence spaces, permissions, external sharing, and content activity metadata",
        icon: FaConfluence,
        category: "SaaS Collaboration",
        saasEnum: "CONFLUENCE",
    },
    {
        id: "google-drive",
        name: "Google Drive",
        description: "View synced Drive file sharing exposure, external access, and risky permission changes (metadata only)",
        icon: FaGoogleDrive,
        category: "SaaS Storage",
        saasEnum: "GOOGLE_DRIVE",
    },
    {
        id: "google-workspace",
        name: "Google Workspace",
        description: "View synced Workspace users, groups, admin events, and org-wide policy metadata",
        icon: SiGooglecloud,
        category: "SaaS Identity",
        saasEnum: "GOOGLE_WORKSPACE",
    },
    {
        id: "microsoft-entra-id",
        name: "Microsoft Entra ID",
        description: "View synced identities, apps, roles, sign-in risks, and permission grants",
        icon: FaMicrosoft,
        category: "SaaS Identity",
        saasEnum: "MICROSOFT_ENTRA_ID",
    },
    {
        id: "okta",
        name: "Okta",
        description: "View synced users, groups, apps, assignments, and authentication events metadata",
        icon: SiOkta,
        category: "SaaS Identity",
        saasEnum: "OKTA",
    },
    {
        id: "slack",
        name: "Slack",
        description: "View synced workspace users, apps, tokens, and admin/security events metadata",
        icon: FaSlack,
        category: "SaaS Collaboration",
        saasEnum: "SLACK",
    },
    {
        id: "notion",
        name: "Notion",
        description: "View synced workspace members, page sharing exposure, and permission changes metadata",
        icon: SiNotion,
        category: "SaaS Collaboration",
        saasEnum: "NOTION",
    },
    {
        id: "dropbox",
        name: "Dropbox",
        description: "View synced file sharing, external access, and risky permission changes (metadata only)",
        icon: FaDropbox,
        category: "SaaS Storage",
        saasEnum: "DROPBOX",
    },
    {
        id: "zendesk",
        name: "Zendesk",
        description: "View synced agents, roles, admin settings changes, and ticket activity metadata",
        icon: SiZendesk,
        category: "SaaS Support",
        saasEnum: "ZENDESK",
    },
    {
        id: "zoom",
        name: "Zoom",
        description: "View synced users, roles, meeting/webinar settings, and admin activity metadata",
        icon: SiZoom,
        category: "SaaS Collaboration",
        saasEnum: "ZOOM",
    },
    {
        id: "salesforce",
        name: "Salesforce",
        description: "View synced users, profiles, permission sets, connected apps, and audit events metadata",
        icon: FaSalesforce,
        category: "SaaS CRM",
        saasEnum: "SALESFORCE",
    },
    {
        id: "trello",
        name: "Trello",
        description: "View synced members, boards, org visibility, and activity metadata",
        icon: FaTrello,
        category: "SaaS Collaboration",
        saasEnum: "TRELLO",
    },
    {
        id: "miro",
        name: "Miro",
        description: "View synced users, teams, board sharing exposure, and activity metadata",
        icon: SiMiro,
        category: "SaaS Collaboration",
        saasEnum: "MIRO",
    },
    {
        id: "git-generic",
        name: "Git (Generic)",
        description: "View synced users, repositories, permissions, and activity metadata from generic Git providers",
        icon: FaGit,
        category: "SaaS Dev Platform",
        saasEnum: "GIT_GENERIC",
    },
    {
        id: "atlassian-admin",
        name: "Atlassian Admin",
        description: "View synced organization directory, SCIM users, policies, and audit events metadata",
        icon: FaAtlassian,
        category: "SaaS Identity",
        saasEnum: "ATLASSIAN_ADMIN",
    },
];

type TenantData = { tenantName?: string };

export default function ConfiguredSaasAppsDetails() {
    const [activeApp, setActiveApp] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [configuredEnums, setConfiguredEnums] = useState<Set<string>>(new Set());

    useEffect(() => {
        const load = async () => {
            try {
                const tenantsResp = await axios.get(`${NGROK}/api/get-all-domains`);
                const tenants = (tenantsResp.data || []) as TenantData[];
                const tenantName = tenants?.[0]?.tenantName;

                if (!tenantName) {
                    setConfiguredEnums(new Set());
                    return;
                }

                const configuredResp = await axios.get(`${NGROK}/api/saas/get-configured-saas-apps`, {
                    params: { tenantName },
                });

                const list = configuredResp?.data?.configuredSaasApps;
                const enums: string[] = Array.isArray(list) ? list.map((v: any) => String(v)) : [];
                setConfiguredEnums(new Set(enums));
            } catch (e) {
                setConfiguredEnums(new Set());
            }
        };

        load();
    }, []);

    const visibleApps = useMemo(() => {
        const onlyConfigured = SAAS_APPS.filter((a) => configuredEnums.has(a.saasEnum));
        if (!searchTerm) return onlyConfigured;
        const q = searchTerm.toLowerCase();
        return onlyConfigured.filter(
            (a) =>
                a.name.toLowerCase().includes(q) ||
                a.description.toLowerCase().includes(q) ||
                a.category.toLowerCase().includes(q)
        );
    }, [searchTerm, configuredEnums]);

    const renderActive = () => {
        switch (activeApp) {
            case "gitlab":
                return <GitlabData onBack={() => setActiveApp(null)} />;
            case "github":
                return <GithubData onBack={() => setActiveApp(null)} />;
            default:
                return null;
        }
    };

    if (activeApp) return renderActive();

    const hasAnyConfigured = configuredEnums.size > 0;

    return (
        <div className="flex-1 space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                        <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                            Configured SaaS Apps
                        </h1>
                        <p className="text-muted-foreground">
                            View and manage synced data from connected SaaS integrations
                        </p>
                    </div>
                </div>

                <div className="relative w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                        placeholder="Search apps..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="input-search-configured-saas"
                    />
                </div>
            </div>

            {!hasAnyConfigured && (
                <div className="text-center py-16">
                    <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No SaaS apps configured</h3>
                    <p className="text-muted-foreground">
                        Configure a SaaS app in the SaaS Apps settings page to view synced data here.
                    </p>
                </div>
            )}

            {hasAnyConfigured && (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {visibleApps.map((app) => {
                            const IconComponent = app.icon;

                            return (
                                <Card
                                    key={app.id}
                                    className="border-border/40 hover-elevate transition-all duration-200 cursor-pointer"
                                    onClick={() => setActiveApp(app.id)}
                                    data-testid={`card-configured-saas-${app.id}`}
                                >
                                    <CardContent className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                                                <IconComponent className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                                            </div>

                                            <Badge
                                                variant="outline"
                                                className="text-sm px-3 py-1 bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
                                            >
                                                Configured
                                            </Badge>
                                        </div>

                                        <div className="space-y-3">
                                            <h3 className="font-semibold text-lg leading-tight" data-testid={`title-${app.id}`}>
                                                {app.name}
                                            </h3>
                                            <p className="text-sm text-muted-foreground leading-relaxed">{app.description}</p>
                                            <Badge variant="secondary" className="text-sm">
                                                {app.category}
                                            </Badge>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    {visibleApps.length === 0 && (
                        <div className="text-center py-12">
                            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">No apps found</h3>
                            <p className="text-muted-foreground">Try adjusting your search terms.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}